import { IntlMessageFormat } from 'intl-messageformat';
import type { PostProcessorModule, TOptions } from 'i18next';

const DEALSTACK_AI_KEY = /^(?:cap-manage[.:])?dealstack_ai\./;
const ICU_ARGUMENT = /\{\s*[^\s{},]+\s*,\s*(?:plural|selectordinal|select)\s*,/;
const I18NEXT_TOKEN = /^\{\{\s*(?:-\s*)?([A-Za-z_$][\w$]*)\s*\}\}/;
const SLOT = '__i18next_';

const messages = new Map<string, IntlMessageFormat>();

/**
 * Rewrites top-level i18next `{{name}}` tokens into ICU slot arguments, so substituted text reaches ICU as a value
 * and is never parsed as ICU. Braces inside ICU arguments and quoted runs are left to ICU.
 */
const toIcuMessage = (
	raw: string
): { message: string; tokens: { name: string; text: string }[] } => {
	const tokens: { name: string; text: string }[] = [];
	let message = '';
	let depth = 0;
	let index = 0;
	while (index < raw.length) {
		const char = raw[index];
		const token = depth === 0 && char === '{' ? I18NEXT_TOKEN.exec(raw.slice(index)) : null;
		if (token) {
			message += `{${SLOT}${tokens.length}}`;
			tokens.push({ name: token[1], text: token[0] });
			index += token[0].length;
			continue;
		}
		let end = index + 1;
		if (char === "'" && "{}'".includes(raw[index + 1] ?? '')) {
			// ICU quoting: '' is one apostrophe; ' before a syntax character quotes up to the next lone '.
			end = raw[index + 1] === "'" ? index + 2 : quotedRunEnd(raw, index + 1);
		} else if (char === '{') {
			depth++;
		} else if (char === '}') {
			depth = Math.max(0, depth - 1);
		}
		message += raw.slice(index, end);
		index = end;
	}
	return { message, tokens };
};

const quotedRunEnd = (raw: string, from: number): number => {
	let index = from;
	while (index < raw.length) {
		if (raw[index] === "'") {
			if (raw[index + 1] !== "'") {
				return index + 1;
			}
			index++;
		}
		index++;
	}
	return index;
};

const icuValues = (options: TOptions, tokens: { name: string; text: string }[]) => {
	const values: Record<string, string | number | boolean | Date> = {};
	for (const [name, parameter] of Object.entries(options)) {
		if (
			typeof parameter === 'string' ||
			typeof parameter === 'number' ||
			typeof parameter === 'boolean' ||
			parameter instanceof Date
		) {
			values[name] = parameter;
		}
	}
	tokens.forEach((token, index) => {
		values[`${SLOT}${index}`] = slotValue(options, token);
	});
	return values;
};

/** What i18next itself substitutes for `{{name}}`, as a string, so ICU inserts it verbatim. */
const slotValue = (options: TOptions, { name, text }: { name: string; text: string }): string => {
	const parameter: unknown = options[name];
	if (parameter === undefined) {
		return Object.prototype.hasOwnProperty.call(options, name) ? '' : text;
	}
	return parameter === null ? '' : String(parameter);
};

const formatIcu = (raw: string, options: TOptions, locale: string): string => {
	const { message, tokens } = toIcuMessage(raw);
	const cacheKey = `${locale}\u0000${message}`;
	let format = messages.get(cacheKey);
	if (!format) {
		format = new IntlMessageFormat(message, locale, undefined, { ignoreTag: true });
		messages.set(cacheKey, format);
	}
	return String(format.format(icuValues(options, tokens)));
};

/**
 * ICU plural/select for `dealstack_ai` copy, which Tolgee's i18next export passes through unchanged. Needs
 * `postProcessPassResolved` to format the raw copy; without it, it returns the string it is given.
 */
export const dealstackAiIcu: PostProcessorModule = {
	type: 'postProcessor',
	name: 'dealstackAiIcu',
	process(value, keys, options) {
		if (!(Array.isArray(keys) ? keys : [keys]).some((key) => DEALSTACK_AI_KEY.test(key))) {
			return value;
		}
		const resolved: unknown = options.i18nResolved;
		const isResolved = typeof resolved === 'object' && resolved !== null;
		const raw =
			isResolved && 'res' in resolved && typeof resolved.res === 'string'
				? resolved.res
				: value;
		if (!ICU_ARGUMENT.test(raw)) {
			return value;
		}
		const usedLng =
			isResolved && 'usedLng' in resolved && typeof resolved.usedLng === 'string'
				? resolved.usedLng
				: undefined;
		try {
			return formatIcu(raw, options, usedLng ?? options.lng ?? 'en');
		} catch {
			return value;
		}
	},
};
